/**
 * admin-help-center D4: the stable article id registry.
 *
 * Every help article has a permanent kebab-case id. Other surfaces (the
 * onboarding card's Learn rows, any later "Learn more" door) link to an article
 * only through these ids and `helpArticlePath`, so a typo is a type error.
 *
 * Ids are permanent: retitling an article never changes its id. A retired
 * article keeps its id here and is listed in `RETIRED_HELP_ARTICLE_IDS`, so an
 * old link renders the not-available state instead of breaking.
 *
 * The webapp content test asserts the article files and this list match
 * exactly. Browser-safe: zero imports.
 */

export const HELP_AREAS = [
  { key: 'start', name: 'Getting started' },
  { key: 'availability', name: 'Hours and availability' },
  { key: 'services', name: 'Services' },
  { key: 'events', name: 'Events' },
  { key: 'calendar', name: 'Bookings and the calendar' },
  { key: 'clients', name: 'Clients' },
  { key: 'tags', name: 'Tags' },
  { key: 'notes', name: 'Notes' },
  { key: 'forms', name: 'Forms' },
  { key: 'payments', name: 'Payments and refunds' },
  { key: 'chat', name: 'Chat and messages' },
  { key: 'settings', name: 'Settings and profile' },
] as const;

export type HelpAreaKey = (typeof HELP_AREAS)[number]['key'];

export const HELP_ARTICLE_IDS = [
  // Getting started
  'welcome',
  'booking-channel',
  'home-tour',
  'menu-tour',
  'setup-checklist',
  'client-experience',
  'share-your-link',
  // Hours and availability
  'weekly-hours',
  'time-off',
  'booking-window',
  'buffers',
  'daily-limits',
  'pause-bookings',
  'timezone',
  // Services
  'create-service',
  'go-live',
  'service-photos',
  'durations-pricing',
  'approval',
  'reminders-follow-ups',
  'preparation-notes',
  'service-own-hours',
  'duplicate-archive',
  // Events
  'events-intro',
  'create-event',
  'schedule-event',
  'edit-one-date',
  'roster-attendance',
  'hide-open-dates',
  'waitlist',
  // Bookings and the calendar
  'calendar-tour',
  'book-for-client',
  'approve-request',
  'reschedule',
  'cancel-booking',
  'client-cancels',
  'attendance',
  'conflicts',
  'pinned-booking',
  // Clients
  'clients-list',
  'client-page',
  'visitors',
  'merge-delete-client',
  // Tags
  'tags-intro',
  'create-tag',
  'tag-unlocks',
  'tag-awards',
  'assign-remove-tags',
  'tag-holders',
  // Notes
  'notes-intro',
  'write-note',
  'note-templates',
  'note-reminders',
  'pin-notes',
  'find-notes',
  // Forms
  'forms-intro',
  'registration-form',
  'build-form',
  'attach-form',
  'send-form',
  'read-answers',
  // Payments and refunds
  'connect-stripe',
  'cash-only',
  'payment-options',
  'refund-policy',
  'refund-a-client',
  'payments-page',
  // Chat and messages
  'chats-intro',
  'chats-list',
  'secure-chat',
  'admin-alerts',
  'alert-history',
  'client-messages',
  // Settings and profile
  'business-profile',
  'app-look',
  'get-help',
] as const;

export type HelpArticleId = (typeof HELP_ARTICLE_IDS)[number];

/**
 * Ids whose article was retired. They stay in `HELP_ARTICLE_IDS` forever and
 * render the not-available state. Empty until an article is retired.
 */
export const RETIRED_HELP_ARTICLE_IDS: readonly HelpArticleId[] = [];

/** The five articles the onboarding card's "Learn what else Telify does" rows open. */
export const ONBOARDING_HELP_ARTICLE_IDS = [
  'forms-intro',
  'chats-intro',
  'events-intro',
  'tags-intro',
  'notes-intro',
] as const satisfies readonly HelpArticleId[];

/** The help center home. */
export const HELP_HOME_PATH = '/admin/help';

/** The admin path of one article (without the `/webapp` basename). */
export function helpArticlePath(id: HelpArticleId): string {
  return `${HELP_HOME_PATH}/${id}`;
}

const HELP_ARTICLE_ID_SET: ReadonlySet<string> = new Set(HELP_ARTICLE_IDS);

/** Narrows an arbitrary string (a route param) to a registered id. */
export function isHelpArticleId(value: string): value is HelpArticleId {
  return HELP_ARTICLE_ID_SET.has(value);
}
